import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  ButtonGroup,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
  Box,
  DataTable,
} from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "configureStore";
import { useTranslation } from "react-i18next";
import { API_URL } from "constants.js";
import {
  setDestroy,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import DatePicker from "features/datepicker/date-picker";
import useIsPhone from "features/files/use-is-phone";
import CustomFieldsRenderer from "components/customer/build-pdf/components/CustomFieldsRenderer";
import { useCustomFields } from "components/customer/build-pdf/hooks/useCustomFields";
import {
  customFieldsToPayload,
  validateForm,
} from "components/customer/build-pdf/build-pdf-utils";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";
import type { CustomField } from "types/CustomField";
import type { Product } from "types/Product";
import {
  createLeaveRequest,
  fetchLeaveRequestById,
  updateLeaveRequest,
  type SerializedLeaveRequest,
} from "services/leave-requests.service";
import {
  resolveLeaveTemplatesForType,
  type LeaveTemplateInfo,
} from "./resolveLeaveTemplateClient";
import { LeaveTimeRangeSelector } from "./LeaveTimeRangeSelector";
import {
  FIXED_LEAVE_TYPE_CODES,
  MAX_LEAVE_DAYS,
  leaveCalendarLeaveTypeI18nKey,
} from "./leaveCalendar.constants";
import {
  isLeaveRangeTooLong,
  maxLeaveEndDate,
} from "./leaveCalendarDates";
import { leaveApiErrorKey } from "./leaveApiErrors";
import { leaveTypeCodeToVisual } from "./leaveTypeMapping";
import { mergeRealtimeLeaveUpdate } from "./leaveRecordMappers";
import { fetchById as fetchFileById } from "services/upload-file.service";
import { bumpLeaveCalendarRefresh } from "./leaveCalendarSlice";
import type { CalendarUser, LeaveRecord } from "./leaveCalendar.types";
import { socket } from "services/socket-client";
import { post as clickToSign, declineSignRequest } from "services/clicktosign.service";
import { clickSignErrorKey } from "utils/click-sign-errors";
import { toast } from "sonner";
import { LinkIcon } from "@shopify/polaris-icons";
import { copyLeaveCalendarShareLink } from "./leaveCalendarShareLink";

export interface LeaveCalendarLeaveModalProps {
  mode: "create" | "view";
  companyId?: string;
  users?: CalendarUser[];
  startDate?: string;
  endDate?: string;
  leave?: LeaveRecord | null;
  settings?: { saturdayOff: boolean; sundayOff: boolean };
  holidays?: { date: string; nameEn: string; nameLt: string }[];
  allLeaves?: LeaveRecord[];
  /** Opened outside the company calendar (e.g. the employee card): no editing,
   *  because overlap / day-off validations need the calendar's context. */
  readOnly?: boolean;
}

function close(dispatch: ReturnType<typeof useDispatch>) {
  dispatch(setIsModalxOpen(false));
  dispatch(setDestroy());
}

function parseTimeToMinutes(value: string): number | null {
  if (!value || !value.includes(":")) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function resolveReferenceYear(...dateValues: Array<string | undefined | null>): number {
  for (const raw of dateValues) {
    const value = String(raw || "").trim();
    if (!value) continue;
    const yearPart = Number(value.split("-")[0]);
    if (Number.isInteger(yearPart) && yearPart >= 1900 && yearPart <= 3000) {
      return yearPart;
    }
  }
  return new Date().getFullYear();
}

const LeaveCalendarLeaveModal: React.FC<LeaveCalendarLeaveModalProps> = ({
  mode: initialMode,
  companyId,
  users = [],
  startDate: initialStart,
  endDate: initialEnd,
  leave,
  readOnly = false,
  settings = { saturdayOff: true, sundayOff: true },
  holidays = [],
  allLeaves = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  // Sign, decline and the other actions are the point of this window: a finger needs the full-size button.
  const actionButtonSize = useIsPhone() ? "large" : "slim";
  const sessionUserId = useSelector(
    (s: RootState) => s.authReducer.role?.user || ""
  );

  const [activeLeave, setActiveLeave] = useState<LeaveRecord | SerializedLeaveRequest | null>(() => leave || null);
  const leaveRealtimeUpdateVersionRef = useRef(0);
  const [isEditing, setIsEditing] = useState(initialMode === "create");
  const [templates, setTemplates] = useState<LeaveTemplateInfo[]>([]);
  const [isLeaveLoading, setIsLeaveLoading] = useState(
    initialMode === "view" && Boolean(companyId && leave?.id)
  );
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  // Today at midnight for past-date guard
  const todayDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  
  const [start, setStart] = useState(() => activeLeave?.startDate || initialStart || "");
  const [end, setEnd] = useState(() => activeLeave?.endDate || initialEnd || "");
  const [leaveType, setLeaveType] = useState(() => (activeLeave as any)?.leaveType || (activeLeave as any)?.leaveTypeCode || FIXED_LEAVE_TYPE_CODES[0]);
  const [duration, setDuration] = useState<"full_day" | "half_day" | "hourly">(() => (activeLeave?.duration as any) || "full_day");
  const [startTime, setStartTime] = useState(() => activeLeave?.startTime || "");
  const [endTime, setEndTime] = useState(() => activeLeave?.endTime || "");

  const [templateId, setTemplateId] = useState("");
  const [signType, setSignType] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productGroups, setProductGroups] = useState<any[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Inline sign/decline state
  const [signAction, setSignAction] = useState<null | 'sign' | 'decline'>(null);
  const [inlineDeclineReason, setInlineDeclineReason] = useState("");
  const [isSignActionLoading, setIsSignActionLoading] = useState(false);
  const [signActionError, setSignActionError] = useState<string | null>(null);

  /** Document label from leave row / fetch-by-id file object. */
  const fileDisplayName = useMemo(() => {
    const al = activeLeave as SerializedLeaveRequest | LeaveRecord | null;
    const label = al?.file?.title || al?.file?.name;
    return label && String(label).trim() ? String(label).trim() : "";
  }, [activeLeave]);

  /** Template id from the generated file (build-pdf parity); enables useCustomFields edit branch. */
  const [templateIdByFetch, setTemplateIdByFetch] = useState("");
  /** After user changes leave type, allow template list to drive templateId again. */
  const [leaveTypeUserTouched, setLeaveTypeUserTouched] = useState(false);

  const fileId = useMemo(() => {
    return (activeLeave as any)?.fileId || (activeLeave as any)?.file;
  }, [activeLeave]);

  const leaveRequestId = (activeLeave as LeaveRecord | SerializedLeaveRequest | null)?.id;
  const isHydratingFromLeaveFile = Boolean(
    leaveRequestId && fileId && isEditing
  );

  const {
    customFields,
    fileEntitiesMap,
    handleCustomFieldChange,
    transformCustomFields,
    setCustomFields,
    setTemplateEntity,
    setInitialCustomFieldsWhenEditLoaded,
    setInitialTemplateEntityWhenEditLoaded,
  } = useCustomFields({
    companyId,
    templateId,
    templateIdByFetch,
    isEditMode: isHydratingFromLeaveFile,
    onSignTypeDetected: setSignType,
  });

  const directDownloadUrl = useMemo(() => {
    if (fileId && companyId) {
      return `${API_URL}/${companyId}/files/download/${fileId}`;
    }
    return null;
  }, [fileId, companyId]);

  useEffect(() => {
    setLeaveTypeUserTouched(false);
    setTemplateIdByFetch("");
  }, [leave?.id]);

  // Keep form state in sync with freshly fetched leave data while in view mode.
  // This ensures "Edit" opens with up-to-date values after fetch-by-id completes.
  useEffect(() => {
    if (isEditing) return;
    if (!activeLeave) return;
    setStart(activeLeave.startDate || initialStart || "");
    setEnd(activeLeave.endDate || initialEnd || "");
    setLeaveType(
      (activeLeave as any)?.leaveType ||
        (activeLeave as any)?.leaveTypeCode ||
        FIXED_LEAVE_TYPE_CODES[0]
    );
    setDuration(((activeLeave?.duration as any) || "full_day") as "full_day" | "half_day" | "hourly");
    setStartTime(activeLeave?.startTime || "");
    setEndTime(activeLeave?.endTime || "");
  }, [activeLeave, isEditing, initialStart, initialEnd]);

  // Keep leave modal aligned with other modules: hydrate full entity by id first.
  useEffect(() => {
    if (!companyId || !leave?.id) {
      setIsLeaveLoading(false);
      return;
    }
    let cancelled = false;
    const realtimeUpdateVersion = leaveRealtimeUpdateVersionRef.current;
    setIsLeaveLoading(true);
    (async () => {
      try {
        const { leaveRequest } = await fetchLeaveRequestById(companyId, leave.id);
        if (
          !cancelled &&
          leaveRequest &&
          realtimeUpdateVersion === leaveRealtimeUpdateVersionRef.current
        ) {
          setActiveLeave(leaveRequest);
        }
      } catch (_e) {
        /* keep fallback leave from list row */
      } finally {
        if (!cancelled) {
          setIsLeaveLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, leave?.id]);

  // Real-time updates for open leave requests (sync status/signatures and handle deletion).
  useEffect(() => {
    if (!companyId || !leaveRequestId) return;
    let cancelled = false;

    const onLeaveUpdated = (...args: unknown[]) => {
      const payload = args[0] as { companyId: string; leaveRequest: any } | undefined;
      if (!payload?.leaveRequest || String(payload.companyId) !== String(companyId)) return;
      if (String(payload.leaveRequest.id) === String(leaveRequestId)) {
        // The socket carries the redacted row for everyone: take its status /
        // dates now without dropping details already shown, then re-read the
        // row by id (full data for the employee, their manager and signers).
        const realtimeUpdateVersion = ++leaveRealtimeUpdateVersionRef.current;
        setActiveLeave((prev) => mergeRealtimeLeaveUpdate(prev, payload.leaveRequest));
        fetchLeaveRequestById(companyId, leaveRequestId)
          .then(({ leaveRequest }) => {
            if (
              !cancelled &&
              leaveRequest &&
              realtimeUpdateVersion === leaveRealtimeUpdateVersionRef.current
            ) {
              setActiveLeave(leaveRequest);
            }
          })
          .catch((err: any) => {
            if (!cancelled && err?.response?.status === 404) {
              toast.info(t("leave_calendar_deleted_toast", { defaultValue: "This leave request has been cancelled or deleted." }));
              close(dispatch);
            }
          });
      }
    };

    const onLeaveDeleted = (...args: unknown[]) => {
      const payload = args[0] as { companyId: string; id: string } | undefined;
      if (!payload?.id || String(payload.companyId) !== String(companyId)) return;
      if (String(payload.id) === String(leaveRequestId)) {
        toast.info(t("leave_calendar_deleted_toast", { defaultValue: "This leave request has been cancelled or deleted." }));
        close(dispatch);
      }
    };

    const onReconnect = async () => {
      const realtimeUpdateVersion = leaveRealtimeUpdateVersionRef.current;
      try {
        const { leaveRequest } = await fetchLeaveRequestById(companyId, leaveRequestId);
        if (
          !cancelled &&
          leaveRequest &&
          realtimeUpdateVersion === leaveRealtimeUpdateVersionRef.current
        ) {
          leaveRealtimeUpdateVersionRef.current += 1;
          setActiveLeave(leaveRequest);
        }
      } catch (err: any) {
        if (!cancelled && err?.response?.status === 404) {
          toast.info(t("leave_calendar_deleted_toast", { defaultValue: "This leave request has been cancelled or deleted." }));
          close(dispatch);
        }
      }
    };

    socket.on("leave-updated", onLeaveUpdated as any);
    socket.on("leave-deleted", onLeaveDeleted as any);
    socket.on("reconnect", onReconnect as any);

    return () => {
      cancelled = true;
      socket.off("leave-updated", onLeaveUpdated as any);
      socket.off("leave-deleted", onLeaveDeleted as any);
      socket.off("reconnect", onReconnect as any);
    };
  }, [companyId, leaveRequestId, dispatch, t]);

  // Same as UserDocumentForm / OrderDocumentForm: load template + custom field values from file.
  useEffect(() => {
    if (!companyId || !fileId || !leaveRequestId || !isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFileById(fileId, companyId);
        if (cancelled) return;
        const templateIdRaw = data.templateId;
        const tid =
          typeof templateIdRaw === "object" && templateIdRaw?.id
            ? templateIdRaw.id
            : templateIdRaw;
        if (!tid) return;

        setTemplateId(tid);
        setTemplateIdByFetch(tid);
        if (data.signType) setSignType(data.signType);

        const fileTemplateEntity = await fetchFileTemplateById(companyId, tid);
        if (cancelled) return;

        setTemplateEntity(fileTemplateEntity);
        const transformedFields = await transformCustomFields(
          data.filesTemplateCustomField?.customFields,
          fileTemplateEntity.customFields
        );
        const loadedCustomFields = fileTemplateEntity.customFields.map(
          (field: CustomField) => ({
            ...field,
            value:
              field.type === "file"
                ? transformedFields[field.label] || []
                : transformedFields[field.label] || "",
          })
        );
        setCustomFields(loadedCustomFields);
        setInitialCustomFieldsWhenEditLoaded(loadedCustomFields);
        setInitialTemplateEntityWhenEditLoaded(fileTemplateEntity);
        const ftc = data.filesTemplateCustomField as
          | { products?: Product[]; productGroups?: any[] }
          | undefined;
        setProducts(ftc?.products || []);
        setProductGroups(ftc?.productGroups || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // setCustomFields / setTemplateEntity / setInitial* are stable; transformCustomFields is useCallback([companyId]) in useCustomFields
  }, [companyId, fileId, leaveRequestId, isEditing, transformCustomFields]);

  // Load templates when leave type (or company) changes. When the type changes,
  // the previous templateId usually belongs to another type — we must pick a template
  // from the new list (or keep prev only if it appears in the new list).
  // While editing an existing generated file, template + values come from the file until the user changes leave type.
  useEffect(() => {
    if (!companyId || !leaveType) return;
    const deferTemplatePick = Boolean(
      leaveRequestId && fileId && !leaveTypeUserTouched
    );
    let cancelled = false;
    (async () => {
      try {
        const list = await resolveLeaveTemplatesForType(companyId, leaveType);
        if (cancelled) return;
        setTemplates(list);
        if (deferTemplatePick) return;
        setTemplateId((prev) => {
          if (list.some((tmpl) => tmpl.id === prev)) return prev;
          return list[0]?.id ?? "";
        });
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, leaveType, leaveRequestId, fileId, leaveTypeUserTouched]);

  const typeOptions = useMemo(
    () =>
      FIXED_LEAVE_TYPE_CODES.map((code) => ({
        label: t(leaveCalendarLeaveTypeI18nKey(code), { defaultValue: code }),
        value: code,
      })),
    [t]
  );

  const templateOptions = useMemo(
    () => [...templates.map((tmpl) => ({ label: tmpl.name, value: tmpl.id }))],
    [templates, t]
  );

  const durationOptions = useMemo(
    () => [
      { label: t("leave_calendar_duration_full"), value: "full_day" },
      { label: t("leave_calendar_duration_half"), value: "half_day" },
      { label: t("leave_calendar_duration_hourly"), value: "hourly" },
    ],
    [t]
  );
  const isAdditionalRestTime = leaveType === "additional rest time";
  const isBusinessTripLeave = leaveType === "business trip leave";

  const handleSave = useCallback(async () => {
    if (!companyId) return;
    setSubmitError(null);
    setFieldErrors({});

    // Basic fields validation
    if (!sessionUserId || !start || !end || !leaveType) {
      setSubmitError(t("leave_calendar_validation_required"));
      return;
    }
    if (!templateId) {
      setSubmitError(t("leave_calendar_template_required"));
      return;
    }
    const selectedBusinessTripId = (() => {
      const businessTripField = customFields.find(
        (field) => field.type === "business_trip"
      );
      if (!businessTripField) return "";
      const raw = businessTripField.value;
      if (Array.isArray(raw)) return String(raw[0] || "");
      return String(raw || "");
    })();
    if (isBusinessTripLeave && !selectedBusinessTripId) {
      setSubmitError(
        t("leave_calendar_trip_required", {
          defaultValue: "Business trip selection is required for this leave type",
        })
      );
      return;
    }

    const isOffDay = (dateStr: string) => {
      const date = new Date(dateStr);
      const day = date.getUTCDay();
      if (day === 0 && settings.sundayOff) return true;
      if (day === 6 && settings.saturdayOff) return true;
      return holidays.some((h: any) => h.date === dateStr);
    };

    // Past-date check
    if (start < todayStr) {
      setSubmitError(t("leave_calendar_past_date_error", { defaultValue: "Start date cannot be in the past" }));
      return;
    }

    // Off-day check
    if (isOffDay(start)) {
      setSubmitError(t("leave_calendar_start_off_day", { defaultValue: "Start date cannot be a holiday or weekend" }));
      return;
    }
    if (isOffDay(end)) {
      setSubmitError(t("leave_calendar_end_off_day", { defaultValue: "End date cannot be a holiday or weekend" }));
      return;
    }

    // Overlap check
    const hasOverlap = allLeaves.some(l => {
      if (l.userId !== sessionUserId) return false;
      const leaveId = (activeLeave as any)?.id;
      if (leaveId && l.id === leaveId) return false;
      if (l.status === 'rejected' || l.status === 'cancelled') return false;
      
      return l.startDate <= end && l.endDate >= start;
    });

    if (hasOverlap) {
      setSubmitError(t("leave_calendar_overlap_error", { defaultValue: "You already have a leave request during this period" }));
      return;
    }

    // Start must be <= end
    if (start > end) {
      setSubmitError(t("leave_calendar_start_after_end", { defaultValue: "Start date must be before or equal to end date" }));
      return;
    }
    if (isLeaveRangeTooLong(start, end)) {
      setSubmitError(
        t("leave_calendar_max_days_error", {
          days: MAX_LEAVE_DAYS,
          defaultValue: "A leave request cannot be longer than {{days}} days",
        })
      );
      return;
    }
    if (duration === "half_day" || duration === "hourly") {
      if (!String(startTime || "").trim() || !String(endTime || "").trim()) {
        setSubmitError(t("leave_calendar_half_day_times"));
        return;
      }
      if (duration === "hourly" && start !== end) {
        setSubmitError(t("leave_calendar_hourly_single_date"));
        return;
      }
      const startMinutes = parseTimeToMinutes(String(startTime));
      const endMinutes = parseTimeToMinutes(String(endTime));
      if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) {
        setSubmitError(t("leave_calendar_time_range_invalid"));
        return;
      }
    }

    // Template custom fields validation
    const v = validateForm({
      templateId,
      date: start,
      subject: "user",
      selectedContactIds: [],
      signType: signType || "",
      selectedUser: "",
      selectedNonTechUser: "",
      isSystemSource: false,
      isEditingSystemTemplate: false,
      isAutoBusinessTripDocument: false,
      customFields,
      products,
      productGroups,
    });
    if (Object.keys(v).length > 0) {
      const flat: Record<string, string> = {};
      Object.entries(v).forEach(([k, val]) => {
        flat[k] = typeof val === "string" ? t(val) : t(val.key, val.params);
      });
      setFieldErrors(flat);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        leaveType: leaveType,
        duration,
        startDate: start,
        endDate: end,
        businessTripId: isBusinessTripLeave ? selectedBusinessTripId : undefined,
        startTime: (duration === "half_day" || duration === "hourly") ? String(startTime) : undefined,
        endTime: (duration === "half_day" || duration === "hourly") ? String(endTime) : undefined,
        templateId: templateId || undefined,
        customFields: templateId ? customFieldsToPayload(customFields) : undefined,
      };

      let result: SerializedLeaveRequest;
      if (activeLeave?.id) {
        const res = await updateLeaveRequest(companyId, activeLeave.id, payload);
        result = res.leaveRequest;
      } else {
        const res = await createLeaveRequest(companyId, payload);
        result = res.leaveRequest;
      }

      setActiveLeave(result);
      setIsEditing(false);
      dispatch(bumpLeaveCalendarRefresh());
    } catch (err: any) {
      const code = err?.response?.data?.error;
      // { days } is only consumed by leave_calendar_max_days_error; harmless elsewhere.
      setSubmitError(t(leaveApiErrorKey(code), { days: MAX_LEAVE_DAYS }));
    } finally {
      setIsSubmitting(false);
    }
  }, [companyId, sessionUserId, start, end, leaveType, duration, startTime, endTime, templateId, customFields, activeLeave, signType, products, productGroups, dispatch, t, settings, holidays, allLeaves, isBusinessTripLeave]);

  const handleInlineSign = async () => {
    if (!companyId || !fileId) return;
    setIsSignActionLoading(true);
    setSignActionError(null);
    try {
      await clickToSign(companyId, { fileId });
      dispatch(bumpLeaveCalendarRefresh());
      // Update local file.signersList to reflect signed status
      if (activeLeave && (activeLeave as any).file?.signersList) {
        const updatedSigners = (activeLeave as any).file.signersList.map((s: any) =>
          s.user?.id === sessionUserId ? { ...s, status: 'signed' } : s
        );
        setActiveLeave({
          ...(activeLeave as any),
          file: { ...(activeLeave as any).file, signersList: updatedSigners },
        });
      }
      setSignAction(null);
    } catch (e) {
      setSignActionError(t(clickSignErrorKey(e, "leave_calendar_action_failed")));
    } finally {
      setIsSignActionLoading(false);
    }
  };

  const handleInlineDecline = async () => {
    if (!companyId || !fileId) return;
    setIsSignActionLoading(true);
    setSignActionError(null);
    try {
      await declineSignRequest(companyId, { fileId, declineReason: inlineDeclineReason });
      dispatch(bumpLeaveCalendarRefresh());
      // Update local file.signersList to reflect declined status
      if (activeLeave && (activeLeave as any).file?.signersList) {
        const updatedSigners = (activeLeave as any).file.signersList.map((s: any) =>
          s.user?.id === sessionUserId ? { ...s, status: 'rejected', declineReason: inlineDeclineReason } : s
        );
        setActiveLeave({
          ...(activeLeave as any),
          file: { ...(activeLeave as any).file, signersList: updatedSigners },
        });
      }
      setSignAction(null);
      setInlineDeclineReason("");
    } catch (e) {
      setSignActionError(t(clickSignErrorKey(e, "leave_calendar_action_failed")));
    } finally {
      setIsSignActionLoading(false);
    }
  };

  const handleCopyShareLink = useCallback(async () => {
    const leaveId = (activeLeave as LeaveRecord | SerializedLeaveRequest | null)?.id;
    if (!companyId || !leaveId) return;
    setIsCopyingLink(true);
    try {
      const ok = await copyLeaveCalendarShareLink(companyId, leaveId);
      if (ok) {
        toast.success(
          t("leave_calendar_copy_link_success", {
            defaultValue: "Leave calendar link copied.",
          })
        );
      } else {
        toast.error(
          t("leave_calendar_copy_link_error", {
            defaultValue: "Could not copy link.",
          })
        );
      }
    } finally {
      setIsCopyingLink(false);
    }
  }, [activeLeave, companyId, t]);

  if (initialMode === "view" && isLeaveLoading) {
    return (
      <div
        style={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
          <Spinner size="large" />
      </div>
    );
  }

  // UI rendering - View Mode
  if (!isEditing && activeLeave) {
    const statusKey = `leave_calendar_status_${activeLeave.status || "pending_approval"}`;
    const shareLeaveId = activeLeave.id;
    const isApproved = activeLeave.status === "approved";
    const isRejected = activeLeave.status === "rejected";
    const isCancelled = activeLeave.status === "cancelled";
    const visualType = leaveTypeCodeToVisual(activeLeave.leaveType);

    return (
      <div className="flex flex-col gap-4 p-1">
        {actionError && <Banner tone="critical">{actionError}</Banner>}
        
        {/* Top Header Card */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-50/50 border border-slate-100 p-4 rounded-2xl">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl text-white font-black text-sm shadow-md bg-gradient-to-br ${
              visualType === "vacation" ? "from-emerald-400 to-teal-500" :
              visualType === "business_trip" ? "from-sky-400 to-blue-500" :
              "from-indigo-400 to-purple-500"
            }`}>
              {visualType === "vacation" ? "V" : visualType === "business_trip" ? "K" : "R"}
            </div>
            <div>
              <h4 className="text-sm font-extrabold text-slate-800">
                {users.find((u) => u.id === activeLeave.userId || u.id === (activeLeave as any).employee)?.name || activeLeave.userId}
              </h4>
              <p className="text-[11px] font-semibold text-slate-400 mt-0.5">
                {(activeLeave as any).leaveType
                  ? t(leaveCalendarLeaveTypeI18nKey((activeLeave as any).leaveType), {
                      defaultValue: (activeLeave as any).leaveType,
                    })
                  : "—"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-stretch sm:self-auto justify-between sm:justify-start">
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold shadow-sm border ${
              isApproved ? "bg-emerald-50 border-emerald-100 text-emerald-700 shadow-emerald-100/10" :
              isRejected || isCancelled ? "bg-rose-50 border-rose-100 text-rose-700 shadow-rose-100/10" :
              "bg-amber-50 border-amber-100 text-amber-700 shadow-amber-100/10"
            }`}>
              {t(statusKey)}
            </span>
            {companyId && shareLeaveId && (
              <Button
                icon={LinkIcon as any}
                onClick={handleCopyShareLink}
                loading={isCopyingLink}
                size={actionButtonSize}
              >
                {t("leave_calendar_copy_link", { defaultValue: "Copy link" })}
              </Button>
            )}
          </div>
        </div>

        {/* Date and Times Card */}
        <div className="bg-slate-50/20 border border-slate-100 p-4 rounded-2xl flex flex-col gap-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("leave_calendar_timeframe", { defaultValue: "Timeframe" })}</div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-extrabold text-slate-700">{activeLeave.startDate}</span>
            <span className="text-slate-300 font-light">→</span>
            <span className="text-sm font-extrabold text-slate-700">{activeLeave.endDate}</span>
            {(activeLeave.duration === "half_day" || activeLeave.duration === "hourly") && activeLeave.startTime && activeLeave.endTime && (
              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-lg border border-slate-200/50">
                {activeLeave.startTime}–{activeLeave.endTime}
              </span>
            )}
          </div>
        </div>

        {activeLeave.isRedacted && (
          <p className="text-xs text-slate-500 px-1">{t("leave_calendar_redacted_note")}</p>
        )}

        {fileId && (
          <div className="bg-slate-50/20 border border-slate-100 p-4 rounded-2xl flex flex-col gap-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("leave_calendar_attached_document", { defaultValue: "Attached Document" })}</div>
            <a
              href={directDownloadUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-700 font-extrabold text-sm flex items-center gap-1.5 transition-colors"
            >
              📄 {fileDisplayName || "Download Document"}
            </a>
          </div>
        )}

        {/* Edit Button */}
        {(() => {
          const signers = (activeLeave as any).file?.signersList || [];
          const isFullySigned = signers.length > 0 && signers.every((s: any) => s.status === 'signed');
          
          return !readOnly && !isApproved && !isFullySigned && (activeLeave.userId === sessionUserId || (activeLeave as any).employee === sessionUserId) && activeLeave.startDate >= todayStr ? (
            <div className="flex justify-end mt-1">
              <Button onClick={() => setIsEditing(true)} size={actionButtonSize}>{t("edit")}</Button>
            </div>
          ) : null;
        })()}

        {/* Signers Table for Click signature */}
        {(activeLeave as any).file?.signType === 'Click' && (activeLeave as any).file?.signersList?.length > 0 && (
          <div className="bg-slate-50/20 border border-slate-100 p-4 rounded-2xl mt-1">
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">{t("document_signature")}</Text>
              {signActionError && <Banner tone="critical">{signActionError}</Banner>}
              <DataTable
                columnContentTypes={['text', 'text', 'text']}
                headings={[t('name'), t('status'), t('action')]}
                rows={((activeLeave as any).file?.signersList || []).map((signer: any) => [
                  signer.user?.name || '',
                  signer.status === 'signed'
                    ? <Badge tone="success">{t('signed')}</Badge>
                    : signer.status === 'rejected'
                      ? <Badge tone="critical">{t('declined')}</Badge>
                      : <Badge tone="attention">{t('pending')}</Badge>,
                  signer.status === 'pending' && signer.user?.id === sessionUserId
                    ? (signAction === 'sign'
                      ? (
                        <BlockStack gap="200">
                          <Text as="p" variant="bodySm">{t('are_you_sure_you_want_to_sign_the_file')}</Text>
                          <InlineStack gap="200">
                            <Button size={actionButtonSize} onClick={handleInlineSign} loading={isSignActionLoading}>{t('Yes')}</Button>
                            <Button size={actionButtonSize} onClick={() => setSignAction(null)}>{t('No')}</Button>
                          </InlineStack>
                        </BlockStack>
                      )
                      : signAction === 'decline'
                        ? (
                          <BlockStack gap="200">
                            <TextField
                              label={t('reason_for_decline_signature')}
                              value={inlineDeclineReason}
                              onChange={setInlineDeclineReason}
                              autoComplete=""
                              multiline={2}
                              requiredIndicator
                            />
                            <InlineStack gap="200">
                              <Button size={actionButtonSize} onClick={handleInlineDecline} loading={isSignActionLoading} disabled={!inlineDeclineReason.trim()}>{t('confirm')}</Button>
                              <Button size={actionButtonSize} onClick={() => { setSignAction(null); setInlineDeclineReason(''); }}>{t('cancel')}</Button>
                            </InlineStack>
                          </BlockStack>
                        )
                        : (
                          <ButtonGroup>
                            <Button size={actionButtonSize} onClick={() => setSignAction('sign')}>{t('sign')}</Button>
                            <Button size={actionButtonSize} onClick={() => setSignAction('decline')}>{t('decline')}</Button>
                          </ButtonGroup>
                        )
                    )
                    : '',
                ])}
              />
            </BlockStack>
          </div>
        )}
      </div>
    );
  }

  // Create/Edit mode
  return (
    <BlockStack gap="400">
      {submitError && <Banner tone="critical">{submitError}</Banner>}
      <InlineStack gap="300" wrap>
        <div style={{ minWidth: 160, flex: 1 }}>
          <DatePicker 
            title={t("leave_calendar_modal_start")} 
            selected={start || undefined} 
            disableDatesAfter={null} 
            disableDatesBefore={todayDate as any} 
            readOnly={isAdditionalRestTime}
            onChange={(date: Date | null) => {
              if (date) {
                const dateStr = date.toISOString().split("T")[0];
                const day = date.getUTCDay();
                const isWeekend = (day === 0 && settings.sundayOff) || (day === 6 && settings.saturdayOff);
                const isHoliday = holidays.some(h => h.date === dateStr);
                if (isWeekend || isHoliday) {
                  toast.error(t("leave_calendar_off_day_error_toast", { defaultValue: "You cannot add a leave request on a weekend or holiday" }));
                }
                setStart(dateStr);
                if (end && (end < dateStr || isLeaveRangeTooLong(dateStr, end))) {
                  setEnd(dateStr);
                }
              } else {
                setStart("");
              }
            }}
          />
        </div>
        <div style={{ minWidth: 160, flex: 1 }}>
          <DatePicker
            title={t("leave_calendar_modal_end")}
            selected={end || undefined}
            disableDatesAfter={start ? maxLeaveEndDate(start) : null}
            disableDatesBefore={todayDate as any}
            readOnly={isAdditionalRestTime}
            onChange={(date: Date | null) => {
              if (date) {
                const dateStr = date.toISOString().split("T")[0];
                const day = date.getUTCDay();
                const isWeekend = (day === 0 && settings.sundayOff) || (day === 6 && settings.saturdayOff);
                const isHoliday = holidays.some(h => h.date === dateStr);
                if (isWeekend || isHoliday) {
                  toast.error(t("leave_calendar_off_day_error_toast", { defaultValue: "You cannot add a leave request on a weekend or holiday" }));
                }
                setEnd(dateStr);
              } else {
                setEnd("");
              }
            }} 
          />
        </div>
      </InlineStack>
      <Select
        label={t("leave_calendar_modal_type")}
        options={typeOptions}
        value={leaveType}
        onChange={(v) => {
          setLeaveTypeUserTouched(true);
          setLeaveType(v);
          if (v === "additional rest time") {
            const year = resolveReferenceYear(
              start,
              end,
              activeLeave?.startDate,
              initialStart,
              initialEnd
            );
            const septemberFirst = `${year}-09-01`;
            setStart(septemberFirst);
            setEnd(septemberFirst);
          }
        }}
      />
      
      <Select label={t("leave_calendar_duration_label")} options={durationOptions} value={duration} onChange={(v) => setDuration(v as any)} />
      {(duration === "half_day" || duration === "hourly") && (
        <LeaveTimeRangeSelector
          startTime={String(startTime || "")}
          endTime={String(endTime || "")}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
          startLabel={t("leave_calendar_start_time")}
          endLabel={t("leave_calendar_end_time")}
        />
      )}

      {/* TEMPLATE PICKER AT THE END */}
      <Box paddingBlockStart="200">
        <BlockStack gap="300">
          <Select label={t("select_template")} options={templateOptions} value={templateId} onChange={setTemplateId} />
          {templateId && (
            <CustomFieldsRenderer
              customFields={customFields}
              errors={fieldErrors}
              onFieldChange={handleCustomFieldChange}
              products={products}
              productGroups={productGroups}
              setProducts={setProducts}
              setProductGroups={setProductGroups}
              orderId={null}
              templateId={templateId}
              customerId=""
              fileEntitiesMap={fileEntitiesMap}
              businessTripFilterContext={{
                companyId,
                startDate: start,
                endDate: end,
              }}
            />
          )}
        </BlockStack>
      </Box>
      
      <InlineStack align="space-between" blockAlign="center" wrap>
        <Button onClick={() => (activeLeave ? setIsEditing(false) : close(dispatch))}>{t("cancel")}</Button>
        <Button variant="primary" onClick={handleSave} loading={isSubmitting}>{t("save")}</Button>
      </InlineStack>
    </BlockStack>
  );
};

export default LeaveCalendarLeaveModal;
